import { Input } from "@redotech/ui/form";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as sliderCss from "./slider.module.css";

type SliderSize = "xs" | "sm" | "md" | "lg";

export const Slider = ({
  value,
  onChange,
  min,
  max,
  step,
  label,
  displayValue,
  disabled = false,
  customBackground,
  size = "sm",
}: {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step: number;
  label?: string;
  displayValue?: string;
  disabled?: boolean;
  customBackground?: string;
  size?: SliderSize;
}) => {
  const [percentage, setPercentage] = useState(0);
  if (((max - min) / step) % 1 !== 0) {
    throw new Error("Invalid slider step value");
  }
  const sliderMin = 0;
  const sliderMax = useMemo(() => (max - min) * (1 / step), [max, min, step]);
  const getInputValue = useCallback(
    (outputValue: number) => (outputValue - min) / step,
    [min, step],
  );
  const getOutputValue = useCallback(
    (inputValue: number) => step * inputValue + min,
    [step, min],
  );

  useEffect(() => {
    if (value % step !== 0 || value < min || value > max) {
      const roundedValue = Math.round(value / step) * step;
      onChange(clamp(roundedValue, min, max));
    }
    setPercentage((getInputValue(value) / sliderMax) * 100);
  }, [value, step, min, max, onChange, getInputValue, sliderMax]);

  const getLabelFontSize = (size: SliderSize) => {
    switch (size) {
      case "xs":
        return "12px";
      case "sm":
        return "14px";
      case "md":
        return "16px";
      case "lg":
        return "18px";
      default:
        return "14px";
    }
  };

  const getLabelLineHeight = (size: SliderSize) => {
    switch (size) {
      case "xs":
        return "16px";
      case "sm":
        return "20px";
      case "md":
        return "24px";
      case "lg":
        return "28px";
      default:
        return "20px";
    }
  };

  const getSliderHeight = (size: SliderSize) => {
    switch (size) {
      case "xs":
        return "2px";
      case "sm":
        return "4px";
      case "md":
        return "8px";
      case "lg":
        return "24px";
      default:
        return "4px";
    }
  };

  const getBorderRadius = (size: SliderSize) => {
    switch (size) {
      case "xs":
        return "1px";
      case "sm":
        return "2px";
      case "md":
        return "4px";
      case "lg":
        return "12px";
      default:
        return "2px";
    }
  };

  return (
    <label data-web="Slider">
      {!!(label || displayValue) && (
        <div
          className={sliderCss.label}
          style={{
            fontSize: getLabelFontSize(size),
            lineHeight: getLabelLineHeight(size),
            paddingBottom: size === "xs" ? "2px" : undefined,
          }}
        >
          <span>{label}</span>
          <span>{displayValue}</span>
        </div>
      )}
      <input
        className={sliderCss.slider}
        disabled={disabled}
        id="myRange"
        max={sliderMax}
        min={sliderMin}
        onChange={(e) => onChange(getOutputValue(parseInt(e.target.value)))}
        style={{
          background:
            customBackground ||
            `linear-gradient(to right, black ${percentage}%, #d3d3d3 ${percentage}%)`,
          height: getSliderHeight(size),
          borderRadius: getBorderRadius(size),
        }}
        type="range"
        value={getInputValue(value)}
      />
    </label>
  );
};

export function FormSlider({
  input,
  min,
  max,
  step,
  label,
  displayValue,
  size,
}: {
  input: Input<number>;
  min: number;
  max: number;
  step: number;
  label?: string;
  displayValue?: string;
  size?: SliderSize;
}) {
  return (
    <Slider
      displayValue={displayValue}
      label={label}
      max={max}
      min={min}
      onChange={input.setValue}
      size={size}
      step={step}
      value={input.value}
    />
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}
